/**
 * Normalisation des préfixes téléphoniques — bug « Belgium(+32)488266446 ».
 *
 * Les profils historiques (`users/{uid}.country`) stockent le LABEL du select
 * euroPrefixes (« Belgium (+32) »), pas un indicatif. Ce label fuyait comme
 * « préfixe » via le fallback d'order-summary, était persisté brut par les
 * builders sender de unified-shipment.models, concaténé par la CF
 * customer-profile-sync (`${prefix}${number}`), puis pré-rempli tel quel dans
 * l'envoi assisté. Ces helpers purs savent extraire l'indicatif de toutes les
 * formes rencontrées en base : '+32', 'BE', 'Belgium (+32)', déchet.
 *
 * ⚠️ #1268 — HEURISTIQUES DÉPRÉCIÉES. `normalizeDialCode` de `@befret/core/phone`
 * couvre les mêmes formes en s'appuyant sur libphonenumber, donc sur TOUS les
 * pays au lieu des 13 de la table ci-dessous. Ces helpers restent le temps que
 * leurs quatre appelants migrent (`unified-shipment.models`,
 * `order-summary.component`, `assisted-order-admin`) : ils ne doivent plus
 * décider de la VALIDITÉ d'un numéro, seulement en relire l'indicatif.
 */

/**
 * Codes ISO → indicatifs (même table que unified-shipment.models).
 * @deprecated #1268 — voir `countryCallingCodeForRegion` (`@befret/core/phone`).
 */
const ISO_TO_DIAL: Record<string, string> = {
  CD: '+243', BE: '+32', FR: '+33', NL: '+31', DE: '+49', LU: '+352',
  GB: '+44', ES: '+34', IT: '+39', PT: '+351', CH: '+41', AT: '+43', PL: '+48',
};

/** Indicatifs connus, du plus long au plus court (matcher +243 avant +24). */
const KNOWN_DIAL_CODES = [
  '+352', '+351', '+243', '+242', '+49', '+48', '+44', '+43', '+41',
  '+39', '+34', '+33', '+32', '+31',
];

/**
 * Extrait un indicatif téléphonique (`+XX`) de n'importe quelle forme stockée :
 * - déjà un indicatif : '+32' → '+32'
 * - label UI : 'Belgium (+32)' / 'BE (+32)' → '+32'
 * - code ISO : 'BE' → '+32'
 * - inconnu/déchet → '' (l'appelant choisit son défaut)
 */
export function extractDialCode(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const value = raw.trim();
  if (!value) return '';

  // Indicatif nu (+32, +243…)
  if (/^\+\d{1,4}$/.test(value)) return value;

  // Label contenant « (+XX) » — euroPrefixes: 'Belgium (+32)', short: 'BE (+32)'
  const labelMatch = value.match(/\(\s*\+(\d{1,4})\s*\)/);
  if (labelMatch) return `+${labelMatch[1]}`;

  // Code ISO 2 lettres
  const iso = ISO_TO_DIAL[value.toUpperCase()];
  if (iso) return iso;

  return '';
}

export interface ParsedPhone {
  prefix: string;
  number: string;
}

/**
 * Découpe une chaîne téléphone stockée en { prefix, number }, robuste aux
 * formats pollués :
 * - 'Belgium (+32)488266446' → { '+32', '488266446' }
 * - '+32 488 26 64 46'       → { '+32', '488266446' }
 * - '0488266446'             → { '',    '0488266446' }
 * - déchet sans chiffres     → { '',    '' }
 * Le numéro retourné ne contient QUE des chiffres.
 */
export function parsePhoneString(raw: unknown): ParsedPhone {
  if (typeof raw !== 'string' || !raw.trim()) return { prefix: '', number: '' };
  let value = raw.trim();

  // Forme label : « … (+32)488266446 » — l'indicatif est entre parenthèses,
  // le numéro est ce qui suit la parenthèse fermante.
  const labelMatch = value.match(/\(\s*(\+\d{1,4})\s*\)\s*(.*)$/);
  if (labelMatch) {
    return { prefix: labelMatch[1], number: labelMatch[2].replace(/\D/g, '') };
  }

  // Indicatif connu en tête (longest-first)
  for (const dial of KNOWN_DIAL_CODES) {
    if (value.startsWith(dial)) {
      return { prefix: dial, number: value.substring(dial.length).replace(/\D/g, '') };
    }
  }

  // Commence par + mais indicatif hors liste : heuristique 2-3 chiffres
  // (comportement historique de l'envoi assisté, conservé).
  if (value.startsWith('+')) {
    const match = value.match(/^(\+\d{2,3})(.*)$/);
    if (match) return { prefix: match[1], number: match[2].replace(/\D/g, '') };
  }

  return { prefix: '', number: value.replace(/\D/g, '') };
}
